import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import LandingPage from './components/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './components/Dashboard';
import ProblemList from './components/ProblemList';
import ProblemDetail from './components/ProblemDetail';
import Profile from './components/Profile';
import Leaderboard from './pages/Leaderboard';
import Learn from './pages/Learn';
import Playground from './pages/Playground';
import OAuthCallback from './pages/OAuthCallback';
import OwnerLogin from './pages/OwnerLogin';
import OwnerDashboard from './pages/OwnerDashboard';
import NotFound from './pages/NotFound';
import Error404 from './components/Error404';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Store
import useAuthStore from './store/authStore';

// Styles
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

// Layout Component for Protected Routes
const ProtectedLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

// Layout Component for Public Routes
const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

function App() {
  const { fetchUser, isAuthenticated, isLoading, user } = useAuthStore();

  useEffect(() => {
    // Check for OAuth token in URL (fallback for broken routing)
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const oauthToken = urlParams.get('oauth_token') || hashParams.get('token');
    
    if (oauthToken) {
      console.log('🔄 OAuth token found in URL, processing...');
      localStorage.setItem('token', oauthToken);
      
      // Set token first, then fetch real user profile
      useAuthStore.setState({
        token: oauthToken,
        isAuthenticated: true,
        isLoading: true
      });
      
      // Fetch actual user profile from backend
      fetchUser().then(() => {
        console.log('✅ OAuth login successful, user profile fetched');
        // Clean URL and redirect to dashboard
        window.history.replaceState({}, document.title, '/dashboard');
      }).catch((error) => {
        console.error('Failed to fetch user profile after OAuth:', error);
        
        // Fallback: Create basic user from token if API fails
        try {
          const payload = JSON.parse(atob(oauthToken.split('.')[1]));
          const fallbackUser = {
            id: payload.userId,
            email: 'oauth-user@example.com',
            username: 'OAuth User',
            firstName: 'OAuth',
            lastName: 'User',
            role: 'USER',
            isVerified: true
          };
          
          localStorage.setItem('user', JSON.stringify(fallbackUser));
          useAuthStore.setState({
            user: fallbackUser,
            token: oauthToken,
            isAuthenticated: true,
            isLoading: false
          });
          
          console.log('⚠️ Using fallback user data for OAuth');
          window.history.replaceState({}, document.title, '/dashboard');
        } catch (decodeError) {
          console.error('Failed to decode token:', decodeError);
        }
      });
      
      return;
    }
    
    // Check for existing token and fetch user data
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser && !isAuthenticated) {
      try {
        const userData = JSON.parse(storedUser);
        // Restore user state from localStorage
        useAuthStore.setState({
          user: userData,
          token,
          isAuthenticated: true,
          isLoading: false
        });
      } catch (error) {
        // If stored data is corrupted, fetch fresh data
        fetchUser();
      }
    } else if (token && !isAuthenticated) {
      fetchUser();
    }
  }, [fetchUser, isAuthenticated]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <PublicLayout>
                    <LandingPage />
                  </PublicLayout>
                </PublicRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <PublicLayout>
                    <Login />
                  </PublicLayout>
                </PublicRoute>
              }
            />
            <Route
              path="/oauth/callback"
              element={
                <PublicLayout>
                  <OAuthCallback />
                </PublicLayout>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <PublicLayout>
                    <Register />
                  </PublicLayout>
                </PublicRoute>
              }
            />

            {/* Owner Routes */}
            <Route
              path="/owner/login"
              element={
                <PublicRoute>
                  <OwnerLogin />
                </PublicRoute>
              }
            />
            <Route
              path="/owner/dashboard"
              element={
                <ProtectedRoute>
                  <OwnerDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <ProtectedLayout>
                    <Dashboard />
                  </ProtectedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/problems"
              element={
                <ProtectedRoute>
                  <ProtectedLayout>
                    <ProblemList />
                  </ProtectedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/problems/:slug"
              element={
                <ProtectedRoute>
                  <ProtectedLayout>
                    <ProblemDetail />
                  </ProtectedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/playground"
              element={
                <ProtectedRoute>
                  <ProtectedLayout>
                    <Playground />
                  </ProtectedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <ProtectedLayout>
                    <Leaderboard />
                  </ProtectedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn"
              element={
                <ProtectedRoute>
                  <ProtectedLayout>
                    <Learn />
                  </ProtectedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:username"
              element={
                <ProtectedRoute>
                  <ProtectedLayout>
                    <Profile />
                  </ProtectedLayout>
                </ProtectedRoute>
              }
            />

            {/* 404 Not Found - Catch all route (must be last) */}
            <Route 
              path="*" 
              element={
                <PublicLayout>
                  <Error404 />
                </PublicLayout>
              } 
            />
          </Routes>

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'rgba(15, 23, 42, 0.9)',
                color: '#fff',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
              },
              success: {
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          </div>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;